import React, { createContext, useContext, useState } from 'react';
import { Filter } from '../../views/MapPortal/Tools/QueryToolModal';
import { SymbologyConfig } from '../../types/symbology.types';

export interface SavedFilter {
  name: string;
  expression: Filter[];
}
export interface ColorRamp {
  id: string;
  colors: string[];
}

export interface FieldProperty {
  field: string;
  alias: string;
  visibility: boolean;
  format?: string;
}

interface LayerPropertiesState {
  // LayerSettingsTab states
  id: number | null;
  folderId: string;
  uniqueLayerName: string;
  layerDescription: string;
  scaleRange: { min?: string; max?: string };
  displayOnMap: boolean;
  displayInLegend: boolean;
  downloadable: boolean;
  selectedFeatureIds: string[];
  // P4 — when "select all matching" is active, this CQL_FILTER drives the
  // server-rendered WMS highlight overlay (so a 50k selection highlights with no
  // client geometry). null = highlight by selectedFeatureIds instead.
  selectionCql?: string | null;
  // Colour of the Query/Proximity tool that produced the selection, opacity in
  // its alpha byte. null = the default cyan used by every other selection path.
  highlightColor?: string | null;

  // SymbologyTab states
  fill: string;
  fillType: string;
  fillOpacity: number;
  outline: string;
  outlineType: string;
  outlineOpacity: number;
  outlineWeight: number;
  symbology: string;
  // Typed symbology config — primary persisted shape (mirrors the backend
  // layers.symbology_config JSONB). Phase 1: 'single' is mirrored from the
  // flat fields at save time; 'unique' is the source of truth for its
  // category array (flat columns can't represent it).
  symbologyConfig?: SymbologyConfig | null;
  selectedColumn: string | null;
  selectedNumber: string | null;
  selectedMode: string | null;
  selectedColourRamp?: ColorRamp;
  opacity: number;
  groupRadius: string;
  radiusUnit: string;
  minZoom?: number;
  maxZoom?: number;
  symbologyUniqueKey?: string;
  symbologyUniqueValue?: string;
  uniqueFill: string;
  uniqueFillOpacity: number;
  uniqueOutline: string;
  uniqueOutlineType?: string;
  uniqueOutlineOpacity: number;
  uniqueOutlineWeight: number;
  iconType?: string;
  selectedIcon?: string;
  iconSize?: number;
  iconRotation?: number;
  // US-101662 Scenario 2 vs 3: a "shape" icon supports full Fill / Outline
  // controls; an "image" (user-uploaded PNG/JPG/SVG) does not. This flag
  // drives the conditional disable in SingleSymbology.
  iconUploadKind?: 'shape' | 'image';
  uniqueIconType?: string;
  uniqueIcon?: string;
  uniqueIconSize?: number;
  uniqueIconRotation?: number;
  quantityField?: string;
  classBreaksMode?: string;
  numberOfBreaks?: number;
  colorRamp?: ColorRamp;
  quantityIconType?: string;
  quantityIcon?: string;
  quantityIconSize?: number;
  quantityIconRotation?: number;
  quantityFillOpacity?: number;
  quantityOutline?: string;
  quantityOutlineOpacity?: number;
  quantityOutlineWeight?: number;

  // EffectsTab states
  clustering: boolean;
  spiralOnHover: boolean;
  disableAt: string | null;
  clusterRadius: number;
  clusterColor: string;
  glow: boolean;
  glowColor: string;
  glowStrength: number;
  glowRadius: number;
  greyScale: boolean;

  //LabelsTab states
  labelField?: string;
  displayLabels: boolean;
  useFilters: boolean;
  filter?: SavedFilter[];
  selectedFilter?: string;
  fontFamily: string | null;
  fontSize: string | null;
  labelColor: string;
  labelPosition: string;
  xAxisOffset: number;
  yAxisOffset: number;
  halo: boolean;
  haloColor: string;
  haloStrength: number;
  haloRadius: number;

  // PopupsTab states
  popupsEnabled: boolean;
  popupTitle: string;
  popupTitleUnit: string;
  defaultPosition:
    | 'top'
    | 'left'
    | 'right'
    | 'bottom'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
    | 'leftTop'
    | 'leftBottom'
    | 'rightTop'
    | 'rightBottom';
  popupCustomStyle: string;
  popupStyleId: string | null;

  //Field property states
  fieldProperties?: FieldProperty[];

  setState: React.Dispatch<React.SetStateAction<LayerPropertiesState>>;

  //utils
  isExistingLayer: boolean;
  layerType: string;

  s3key: string;
  layerTitle: string;
  sourceFormat: string;
}

export const defaultLayerPropertiesState: LayerPropertiesState = {
  //LayerSettingsTab states
  id: null,
  folderId: '',
  uniqueLayerName: 'Untitled Layer',
  layerDescription: '',
  scaleRange: { min: undefined, max: undefined },
  displayOnMap: true,
  displayInLegend: true,
  downloadable: true,
  selectedFeatureIds: [],
  selectionCql: null,
  highlightColor: null,

  //SymbologyTab states
  symbology: 'single',
  symbologyConfig: undefined,
  fill: '#AAAAAA',
  fillType: 'solid',
  fillOpacity: 100,
  outline: '#000000',
  outlineType: '',
  outlineOpacity: 100,
  outlineWeight: 1,
  selectedColumn: null,
  selectedNumber: null,
  selectedMode: null,
  selectedColourRamp: undefined,
  opacity: 50,
  groupRadius: '',
  radiusUnit: 'pixels',
  minZoom: undefined,
  maxZoom: undefined,
  symbologyUniqueKey: undefined,
  symbologyUniqueValue: undefined,
  uniqueFill: '#AAAAAA',
  uniqueFillOpacity: 100,
  uniqueOutline: '#000000',
  uniqueOutlineType: undefined,
  uniqueOutlineOpacity: 100,
  uniqueOutlineWeight: 1,
  iconType: undefined,
  selectedIcon: undefined,
  iconSize: 0,
  iconRotation: 0,
  iconUploadKind: undefined,
  uniqueIconType: undefined,
  uniqueIcon: undefined,
  uniqueIconSize: 0,
  uniqueIconRotation: 0,
  quantityField: undefined,
  classBreaksMode: undefined,
  numberOfBreaks: undefined,
  colorRamp: undefined,
  quantityIconType: undefined,
  quantityIcon: undefined,
  quantityIconSize: undefined,
  quantityIconRotation: undefined,
  quantityFillOpacity: 100,
  quantityOutline: '#000000',
  quantityOutlineOpacity: 100,
  quantityOutlineWeight: 1,

  //PopupsTab states
  popupsEnabled: false,
  popupTitle: '',
  popupTitleUnit: '',
  defaultPosition: 'rightTop',
  popupCustomStyle: '',
  popupStyleId: null,

  //EffectsTab states
  clustering: false,
  spiralOnHover: false,
  disableAt: '10',
  clusterRadius: 10,
  clusterColor: '#1677ff',
  glow: false,
  glowColor: '#1677ff',
  glowStrength: 0,
  glowRadius: 10,
  greyScale: false,

  //LablesTab states
  displayLabels: false,
  labelField: undefined,
  useFilters: false,
  filter: undefined,
  selectedFilter: undefined,
  fontFamily: null,
  fontSize: null,
  labelColor: '#1677ff',
  labelPosition: '',
  xAxisOffset: 0,
  yAxisOffset: 0,
  halo: false,
  haloColor: '#1677ff',
  haloStrength: 0,
  haloRadius: 0,

  fieldProperties: [],

  isExistingLayer: false,
  layerType: '',
  layerTitle: 'Untitled Layer',

  s3key: '',
  sourceFormat: '',

  setState: () => {},
};

// Transient UI / selection / query fields that must never ride along on
// Layer Properties save (s3/save-layer rejects unknown keys like matchFilter).
const TRANSIENT_LAYER_CONTEXT_KEYS = [
  'setState',
  'highlightColor',
  'selectedFeatureIds',
  'selectionCql',
  // Query/Proximity result-table selection (not persisted layer config).
  'matchFilter',
  'matchExtent',
  'matchCql',
  'proximityMatch',
  'matchSink',
  'matchOrder',
  'matchInvert',
  'excludedFeatureIds',
  'includedFeatureIds',
  'excludedIds',
  'includedIds',
  'selectAllMatching',
  'reverseActive',
  'displaySelectionOnly',
] as const;

// Drops tool state that isn't a layer property. Use before saving a layer or
// diffing the context for unsaved edits.
export const omitNonLayerProperties = (state: LayerPropertiesState) => {
  const layerProperties = { ...(state as unknown as Record<string, unknown>) };
  for (const key of TRANSIENT_LAYER_CONTEXT_KEYS) {
    delete layerProperties[key];
  }
  return layerProperties as Omit<LayerPropertiesState, 'setState' | 'highlightColor'>;
};

const LayerPropertiesContext = createContext<LayerPropertiesState>(defaultLayerPropertiesState);

export const useLayerProperitesContext = () => {
  const context = useContext<LayerPropertiesState>(LayerPropertiesContext);

  if (context === undefined) {
    throw new Error('useLayerProperitesContext must be used within a LegendContextProvider');
  }
  return context;
};

export const LayerPropertiesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [state, setState] = useState<LayerPropertiesState>(defaultLayerPropertiesState);
  return <LayerPropertiesContext.Provider value={{ ...state, setState }}>{children}</LayerPropertiesContext.Provider>;
};
